import { useState } from "react";
import { Link } from "wouter";
import { useStoreQuery, queries } from "@/lib/store";
import { GlassCard, Avatar, PillTag } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import ComposeNewMessage from "@/components/mnf/ComposeNewMessage";

function relTime(ts: number): string {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.round(d / 7);
  return `${w}w`;
}

export default function Messages() {
  const threads = useStoreQuery(() => queries.messageThreads());
  const me = useStoreQuery(() => queries.currentUser());
  const canCoach = useStoreQuery(() => (me?.id ? queries.canMessageCoachDirectly(me.id) : false));
  const totalUnread = threads.reduce((sum, t) => sum + t.unread, 0);
  const [composeOpen, setComposeOpen] = useState(false);

  return (
    <div className="px-5 pt-4 pb-8" style={{ background: "var(--mnf-cr)", minHeight: "100%" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="mnf-eyebrow">Sister to sister</p>
          <h1 className="mnf-serif" style={{ fontSize: 30, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.05 }}>
            Messages
          </h1>
        </div>
        <button
          onClick={() => setComposeOpen(true)}
          className="flex items-center justify-center"
          style={{ width: 40, height: 40, borderRadius: 14, background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)" }}
          aria-label="Start a new conversation"
          data-testid="button-compose"
        >
          <Icon name="edit" size={20} />
        </button>
      </div>

      {/* Soft summary */}
      <div className="mb-5 flex items-center gap-2 flex-wrap">
        {totalUnread > 0 ? (
          <PillTag tone="coral">{totalUnread} new {totalUnread === 1 ? "note" : "notes"}</PillTag>
        ) : (
          <PillTag tone="fiber">All caught up</PillTag>
        )}
        <PillTag tone={canCoach ? "gold" : "neutral"}>
          {canCoach ? "Direct line to Coach" : "Lydia first, Coach by tier"}
        </PillTag>
      </div>

      {/* Thread list */}
      <div className="space-y-3" data-testid="list-message-threads">
        {threads.map((t) => {
          const isCoachLocked = t.kind === "coach" && !canCoach;
          const isBot = t.kind === "bot";
          // For locked coach row, route to upsell; for everything else, normal chat route.
          const href = isCoachLocked ? "/flourish" : t.route;
          return (
            <Link key={t.threadId} href={href} data-testid={`row-thread-${t.threadId}`}>
              <div>
                <GlassCard organic className="cursor-pointer" padding={14}>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar initials={t.initials} size={48} ringColor={t.ringColor} />
                      {isBot && (
                        <div
                          className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center"
                          style={{
                            width: 18, height: 18, borderRadius: "50%",
                            background: "var(--mnf-purple)", color: "#fff",
                            fontSize: 10, fontWeight: 700, border: "2px solid var(--mnf-cr)",
                          }}
                          aria-label="Sister Lydia, your assistant"
                        >
                          ✦
                        </div>
                      )}
                      {t.kind === "coach" && (
                        <div
                          className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center"
                          style={{
                            width: 18, height: 18, borderRadius: "50%",
                            background: "var(--mnf-gold)", color: "#fff",
                            fontSize: 11, border: "2px solid var(--mnf-cr)",
                          }}
                          aria-label="Coach Kiley"
                        >
                          {isCoachLocked ? (
                            <Icon name="lock" size={10} />
                          ) : (
                            <span style={{ lineHeight: 1 }}>✦</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className="mnf-serif truncate"
                          style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)" }}
                          data-testid={`text-thread-name-${t.threadId}`}
                        >
                          {t.name}
                        </p>
                        <span className="text-xs mnf-body shrink-0" style={{ color: "var(--mnf-tg)" }}>
                          {isCoachLocked ? "" : relTime(t.lastSentAt)}
                        </span>
                      </div>
                      <p className="text-xs mnf-body mb-1 truncate" style={{ color: "var(--mnf-tg)" }}>
                        {isCoachLocked ? "Rooted tier unlocks direct messaging" : t.subtitle}
                      </p>
                      <div className="flex items-center gap-2 min-w-0">
                        <p
                          className="text-sm mnf-body truncate flex-1 min-w-0"
                          style={{
                            color: isCoachLocked
                              ? "var(--mnf-coral)"
                              : t.unread > 0
                              ? "var(--mnf-fd)"
                              : "var(--mnf-tg)",
                            fontWeight: t.unread > 0 || isCoachLocked ? 500 : 400,
                            fontStyle: isCoachLocked ? "italic" : "normal",
                          }}
                          data-testid={`text-thread-preview-${t.threadId}`}
                        >
                          {isCoachLocked ? (
                            <>Upgrade to unlock Coach Kiley · ask Sister Lydia first</>
                          ) : (
                            <>
                              {t.lastFromHannah && <span style={{ color: "var(--mnf-tg)" }}>You: </span>}
                              {t.lastPreview}
                            </>
                          )}
                        </p>
                        {t.unread > 0 && !isCoachLocked && (
                          <span
                            className="flex items-center justify-center mnf-body shrink-0"
                            style={{
                              minWidth: 20, height: 20, padding: "0 6px",
                              borderRadius: 10,
                              background: "#C0784A", color: "#F5F0E8",
                              fontSize: 11, fontWeight: 600, lineHeight: 1,
                            }}
                            data-testid={`badge-unread-${t.threadId}`}
                          >
                            {t.unread}
                          </span>
                        )}
                        {isCoachLocked && (
                          <Icon name="chevron_right" size={16} />
                        )}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="mt-6 mb-2 text-center">
        <p className="text-xs mnf-body italic" style={{ color: "var(--mnf-tg)" }}>
          "Carry one another's burdens." Galatians 6:2
        </p>
      </div>

      {/* Compose new */}
      <ComposeNewMessage open={composeOpen} onClose={() => setComposeOpen(false)} />
    </div>
  );
}
